import typeahead from '/library/typeahead/typeahead-standalone.es.mjs';
import {utilLanguage} from "../util/utilLanguage.js";

/**
 * Result search field with Typeahead functionality
 * https://www.lfi.ch/de/ergebnisabfrage/suche
 */
const search = {

  /**
   * The language
   * @type {string}
   */
  _lang: utilLanguage.get(),

  /**
   * ID for the input field
   * @type {string}
   */
  _typeaheadId: 'fldSearch',

  /**
   * DOM element of the input field
   * @type {HTMLInputElement}
   */
  _inputField: null,

  /**
   * API URL to controller
   * @type {string}
   */
  _apiUrl: '/api/result',

  /**
   * An object mapping language codes to their corresponding search target page URLs.
   * Used for redirecting users to the appropriate language-specific result query page.
   * @type {Object}
   */
  _targetPage: {
    'de': '/de/ergebnisabfrage',
    'fr': '/fr/recherche-de-resultats',
    'it': '/it/ricerca-di-risultati',
    'en': '/en/result-query-system',
  },

  init: function() {
    this._inputField = document.getElementById(this._typeaheadId);
    this._initTypeahead();
    this._initSearchButton();
    this._inputField.focus();
  },

  /**
   * Initializes the search button click handler to trigger a search with the current input value.
   */
  _initSearchButton: function() {
    const form = document.getElementById('search');
    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this._onSubmit(event, null);
      });
      form.addEventListener('reset', (event) => {
        event.preventDefault();
        this._inputField.value = '';
        this._inputField.focus();
      });
    }
  },

  /**
   * Initializes the Typeahead functionality with specific configuration options for the input field.
   */
  _initTypeahead: function() {
    const config = {
      input: this._inputField,
      name: 'search',
      minLength: 3,
      hint: false,
      autoselect: true,
      highlight: true,
      limit: 50,
      debounceRemote: 500,
      source: {
        keys: ['text'],
        remote: {
          url: this._apiUrl + '/search?q=%QUERY',
          wildcard: '%QUERY',
        },
        groupKey: "source"
      },
      templates: {
        notFound: this._createNotFoundMessage.bind(this),
      },
      preventSubmit: true,
      onSubmit: this._onSubmit.bind(this),
      display: this._display.bind(this)
    };

    // https://typeahead.digitalfortress.tech/
    typeahead(config);
  },

  /**
   * Handles the display logic for a specific element and event.
   * If the event is a click, it triggers the `_onSubmit` method with the provided event and element.
   * For a "Tab" key event, it prevents the input value from being replaced with the item value.
   * Otherwise, returns the `text` property of the element.
   *
   * @param {HTMLElement} element - The HTML element associated with the event.
   * @param {Event} event - The event object representing the interaction.
   * @returns {string} - Returns the input value for a "Tab" key event or the element's text content otherwise.
   */
  _display: function(element, event) {
    if (event && event.type === 'click') {
      this._onSubmit(event, element);
    }
    // prevent tab key from replacing the input value with the item value.
    return (event && event.key === 'Tab') ? event.target.value : element.text;
  },

  /**
   * Handles the submit event for the Typeahead element.
   * If no suggestion is selected (e.g. user pressed Enter with a custom term),
   * the current input field value is used as the search term.
   *
   * @param {Event} event - The event object associated with the submit action.
   * @param {Object} element - The element containing the text value to be used in the query string.
   */
  _onSubmit: function(event, element) {
    const searchTerm = (element && element.text) ? element.text : this._inputField.value;
    window.location.href = this._targetPage[this._lang] + '?p=search&pg=1&fldSearch=' + searchTerm;
  },

  /**
   * Creates a localized "not found" message based on the current language setting.
   *
   * @returns {string} A localized "not found" message.
   */
  _createNotFoundMessage: function() {
    const NOT_FOUND_MESSAGES = {
      de: 'Nichts gefunden',
      fr: 'Rien trouvé',
      it: 'Non è stato trovato nulla',
      en: 'No results found'
    };

    return NOT_FOUND_MESSAGES[this._lang] || NOT_FOUND_MESSAGES.en;
  }
};

search.init();